import postDonorDetails from '@/components/FormSection/postDonorDetails'
import buildErrorsObj from '@/components/FormSection/buildErrorsObj'
import dataLayer from '@/components/dataLayerHelper'
import { SignUpStatus, type DonorDetails } from '@/types'

export async function sendDetails(
  formState: any,
  v$: any,
  $externalResults: any,
  postingDonorDetails: any,
  emit: any,
  router: any
) {
  await v$.value.$validate()

  if (v$.value.$invalid) return

  try {
    postingDonorDetails.value = true

    const token = await getCaptchaToken()
    await sendCaptchaToken(token)

    const response = await postDonorDetails(
      `${import.meta.env.VITE_CORE_BASE_URL}/signup`,
      donorDetails(formState)
    )

    // Check if errors from core are returned
    if (response && 'status' in response && response.status !== 200) {
      dataLayer.sendAccountSubmitFail()

      // Add external errors to $externalResults to report back to user
      if ('errors' in response && Array.isArray(response.errors)) {
        $externalResults.value = {
          ...$externalResults.value,
          ...buildErrorsObj(response.errors),
        }
      }

      return
    }

    // Check sign up status
    if (response && 'signUpStatus' in response) {
      dataLayer.sendAccountSubmitSuccess()

      if (response.signUpStatus === SignUpStatus.EmailSent) {
        sessionStorage.setItem('email', formState.emailAddress)
        emit('submit')
        return
      }

      if (response.signUpStatus === SignUpStatus.Complete) {
        router.push({ name: 'confirmed' })
        return
      }
    } else {
      throw new Error("'signUpStatus' not found in response")
    }
  } catch (err) {
    console.error(err)
    dataLayer.sendAccountSubmitFail()
    $externalResults.value = buildErrorsObj(['Failed to send details'])
    throw new Error('Failed to send details')
  } finally {
    postingDonorDetails.value = false
  }

  return {
    formState,
    v$,
    $externalResults,
    postingDonorDetails,
  }
}

const getCaptchaToken = async () => {
  // @ts-ignore
  // eslint-disable-next-line no-undef
  const captcha = grecaptcha

  return new Promise<string>((resolve) => {
    captcha.ready(() => {
      captcha
        .execute(import.meta.env.VITE_RECAPTCHA_SITE_KEY, {
          action: 'submit',
        })
        .then(resolve)
    })
  })
}

const sendCaptchaToken = async (token: string) => {
  await fetch(import.meta.env.VITE_RECAPTCHA_DOMAIN, {
    method: 'POST',
    body: token,
  })
}

const donorDetails = (formState: DonorDetails): DonorDetails => {
  const details: DonorDetails = {
    emailAddress: formState.emailAddress.toLowerCase().trim(),
    firstname: formState.firstname.trim(),
    lastname: formState.lastname.trim(),
    addressLine1: formState.addressLine1.trim(),
    addressLine2: formState.addressLine2.trim(),
    city: formState.city.trim(),
    county: formState.county.trim(),
    postcode: formState.postcode.toUpperCase().replace(/ /g, '').trim(),
    country: formState.country.trim(),
  }

  if (formState.emailSignature) {
    details.emailSignature = formState.emailSignature.trim()
  }

  return details
}
